import type { NavigationGuard } from 'vue-router'

import { controllerHttp } from '@/shared/http'

// Cancel-on-navigation, ported from v3 (legacy router/index.js:39,60–67; contract 05 §3.1 row 5,
// golden suite "branch 5"): abort the previous navigation's AbortController and bind a fresh
// signal that every controller request inherits, so in-flight report calls die on navigation
// and reject as ApiError{kind:'canceled'} — swallowed by module 03, never toasted.
// This file is the ONE sanctioned `controllerHttp` importer outside api/ (contract 03 §11).
//
// The CONTRACT is ported; one part of the mechanism deliberately is NOT (standing rule — v4 fixes
// what v3 got wrong). v3 cancelled on every navigation, same-route ones included, because v3 had no
// same-route URL syncing to speak of. v4 does, and there the blanket cancel is actively wrong — see
// the same-route guard below. `guard-parity.spec.ts` "branch 5" exercises the v3 REPLICA, not this
// guard, and only over a cross-route hop; the divergence is intentional, not an oversight.
let navController = new AbortController()

export const cancelInFlightGuard: NavigationGuard = (to, from) => {
  // Staying on the same route is a URL SYNC, not a navigation: a tab segment being filled in, a
  // filter landing in the query. The reads in flight belong to the page we are not leaving, and
  // `createHttpClient` composes this signal into every controller request — so cancelling here
  // kills a request the current page has just started. That is the whole of ORP-CREATIVE-AI's
  // "open an analysis and the results never load": the detail page pushes without a `tab`, its
  // panel starts reading, then the corrective `router.replace` lands and aborts it.
  //
  // Nothing leaks by staying out of the way: a component that no longer needs its read aborts on
  // scope dispose, and `useReportData` supersedes its own in-flight call whenever the spec changes.
  // The cross-page cancellation this guard exists for is unchanged.
  if (from.name !== undefined && from.name !== null && to.name === from.name) return true

  navController.abort()
  navController = new AbortController()
  controllerHttp.defaults.signal = navController.signal
  return true
}
